import React, { useEffect, useMemo, useState } from 'react';
import { IconButton, Icon, TextField, Tooltip, Dialog, AppBar, Toolbar, Typography, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useStyles from './TaskDetail.styles';
import { useDispatch, useSelector } from 'react-redux';
import { get, size, first, set, flatten, upperFirst, uniqBy } from 'global/lib/lodash';
import { formatDateFromNow } from 'global/utils/formatDate';
import { getTaskStatus, stopProp } from 'global/utils';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Comment from 'global/components/comment';
import sendData from 'redux/actions/sendData';
import Confirm from 'global/components/confirm';
import { setView } from 'redux/actions/view';
import { useUser } from 'global/hooks/useUser';
import { Text } from 'global/components/text';
import { Row } from 'global/components/row';
import { UploadFiles } from 'redux/actions/imageUploader';
import { Button } from '../../../global/components/Button';
import { normalizeResp, taskStringToNum } from '../../../global/utils';
import { useScreen } from '../../../global/styles';
import { userHasPermissions } from 'redux/actions/userLogin';
import { Includes } from '../TaskListSC/TaskListSC';
import { validateRequiredCustomFields } from '../task-type-utils';
import { isImgOrVid, splitMediaByType } from 'global/utils/mediaUtils';
import SignaturePad from 'global/components/signature-pad';
import { Checkbox } from 'global/components/checkbox';
import Form from 'global/components/form';
import SnackAlerts from 'global/components/snack-alerts';
import useRetrieveData from 'global/hooks/useRetrieveData';

export const allowedToEdit = (task, comments = [], opts = {}) => {
  opts = {
    user: null,
    ...opts,
  };

  const authors = get(task, 'authors', []);
  const assignees = get(task, 'assignees', []);
  const isAuthor = authors.find((a) => a.id === get(opts, 'user.me.id'));
  const isAssignee = assignees.find((a) => a.id === get(opts, 'user.me.id'));

  const obj = {
    show: false,
    disabled: false,
    delete: false,
    status: 10,
    isAuthOrAdmin: false,
  };

  if (!task) {
    return obj;
  }

  const isAdmin = userHasPermissions(['manageAccount']);

  if (isAuthor || isAdmin) {
    obj.isAuthOrAdmin = true;
  }

  if (isAuthor || isAssignee) {
    obj.status = task.status;
  }

  if (isAuthor) {
    obj.delete = true;
  }

  if (size(comments)) {
    obj.status = 10;
  }

  const userRealmId = Number(get(opts, 'user.userRealm.id'))
  if (userHasPermissions(['manageAccount']) && (!isAuthor || !isAssignee)) {
    obj.status = userRealmId === get(task, 'clientId') && userRealmId === get(task, 'vendorId') ? task.status : 10;
    obj.delete = true;
  } else if (!isAuthor && !isAssignee && !userHasPermissions(['manageAccount'])) {
    obj.disabled = !(userRealmId === get(task, 'clientId') && userRealmId === get(task, 'vendorId'));
  }

  obj.show = true;
  return obj;
};

export const CommentContainer = (props) => {
  const { formState, data, task, onSubmitComment, onCustomFieldErrors } = props;

  const comments = useMemo(() => props.comments || [], [props.comments]);

  const { isMobile } = useScreen();
  const classes = useStyles({ ...props, isMobile });

  let [createComment, setCreateComment] = useState();
  const [confirmProps, setConfirmProps] = useState({ maxWidth: 'sm' });
  const photoDrop = true;

  const dispatch = useDispatch();
  const uhook = useUser();
  const { me } = uhook;
  const dontAskTaskDelete = useSelector((state) => get(state, 'view.dontAskTaskDelete'));
  const status = getTaskStatus(task);
  const taskType = get(props, 'taskType');
  const emailBehavior = get(taskType, 'closeout_requirements.status_change_email', 'send_by_default');
  const [sendReceipt, setSendReceipt] = useState(
    emailBehavior !== 'do_not_send_by_default' && emailBehavior !== 'never_send'
  );
  const [emailFormState, setEmailFormState] = useState({});
  const [alerts, setAlerts] = useState();
  const [state, setState] = useState({
    display: 'comment',
    loading: false,
    comments: [],
  });

  const realmId = get(task, 'realm.id');
  const usersList = useRetrieveData(
    { url: '/users' },
    {
      waitFor: realmId,
      build: ['users'],
      params: { filter: { content_realm_id: realmId } },
      useCache: { reload: 1 },
    }
  );

  const ate = allowedToEdit(task, comments, { user: uhook });

  const submitComment = async () => {
    const comms = [...comments, createComment];
    const _comms = comms.filter((comment) => comment.isNew);

    // Collect notify_users from inline email recipients
    const isStatusChange = _comms.some((c) => c.isCorrection);
    const recipients = get(emailFormState, 'emailRecipients', []);
    const assigneeOverride = get(emailFormState, 'newAssignee');
    // Merge new assignees into email recipients so newly assigned users get notified
    const existingIds = new Set(recipients.map((u) => u.id));
    const mergedRecipients = [...recipients];
    if (assigneeOverride && size(assigneeOverride)) {
      assigneeOverride.forEach((u) => {
        if (!existingIds.has(u.id)) mergedRecipients.push(u);
      });
    }
    const effectiveSendReceipt = emailBehavior === 'always_send' ? true
      : emailBehavior === 'never_send' ? false
      : sendReceipt;
    const notify_users = isStatusChange && effectiveSendReceipt && size(mergedRecipients)
      ? mergedRecipients.map((u) => u.id)
      : undefined;

    if (!onSubmitComment) {
      try {
        setState((s) => ({ ...s, loading: true }));
        const task = get(props, 'task', {});
        const currentTaskStatus = getTaskStatus(task);
        const tid = get(task, 'id');

        const result = await Promise.all(
          _comms.map(async (comment) => {
            const stat = taskStringToNum(comment.newStatus);

            // Collect all images including signature
            const commentImages = [...get(comment, 'images', [])];
            const sigData = get(comment, 'signature.data');
            if (sigData) {
              const blob = await (await fetch(sigData)).blob();
              const sigFile = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
              sigFile.path = sigFile.name;
              commentImages.push({ file: sigFile, name: sigFile.name, collection: 'signature-images' });
            }

            // Build comment payload with only API-relevant fields
            const _data = {
              data: {
                type: 'comments',
                attributes: {
                  user_id: get(me, 'id'),
                  commentable_id: tid,
                  commentable_type: 'task',
                  body: get(comment, 'body', ''),
                  images: commentImages,
                  files: get(comment, 'files', []),
                  meta: {
                    showCorrection: stat !== currentTaskStatus.status,
                    status: stat,
                  },
                },
              },
            };

            // Upload all images (photos + signature) and set relationships
            await UploadFiles(_data, { attributeType: 'images' });

            if (size(get(comment, 'files', []))) {
              await UploadFiles(_data, { attributeType: 'files', isImage: false, collection_name: 'comment-files' });
            }

            const taskPatchData = {
              data: {
                id: tid,
                type: 'tasks',
                attributes: {
                  status: stat,
                  ...(notify_users ? { notify_users } : {}),
                },
                ...(assigneeOverride && size(assigneeOverride) ? {
                  relationships: {
                    assignees: {
                      data: assigneeOverride.map((u) => ({ type: 'users', role: 'assignee', id: u.id })),
                    },
                  },
                } : {}),
              },
            };
            const t = await sendData({
              url: `/tasks/${tid}?include=${Includes.join(',')}`,
              method: 'patch',
              data: taskPatchData,
            });

            const com = await sendData({
              url: `/comments`,
              method: 'post',
              data: _data,
            });

            return { comment: com, task: normalizeResp(t) };
          })
        );

        if (notify_users) {
          setAlerts({ message: 'Email sent', severity: 'success' });
        }

        return props.onClose && props.onClose(result);
      } catch (err) {
        return err;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    }
    await onSubmitComment(comms, notify_users, assigneeOverride);
    if (notify_users) {
      setAlerts({ message: 'Email sent', severity: 'success' });
    }
    setCreateComment();
  };

  const clickComment = (opts = {}) => {
    createComment = {
      isNew: true,
      showToggle: false,
      isCorrection: true,
      newStatus: 'closed',
      body: '',
      ...opts,
    };
    setCreateComment({ ...createComment });
  };

  const onFilesChange = (files) => {
    const { images, documents } = splitMediaByType(files);
    set(createComment, 'images', images);
    set(createComment, 'files', documents);
    setCreateComment({ ...createComment });
  };

  const deleteMedia = (file, index) => {
    const allMedia = [...get(createComment, 'images', []), ...get(createComment, 'files', [])];
    allMedia.splice(index, 1);
    const { images, documents } = splitMediaByType(allMedia);
    set(createComment, 'images', images);
    set(createComment, 'files', documents);
    setCreateComment({ ...createComment });
  };

  const closeoutClick = () => {
    const closeConfirm = () => {
      setConfirmProps((cp) => ({ ...cp, title: '' }));
    };

    const deleteImages = async () => {
      try {
        await Promise.all(
          allImages().map(async (ci) => {
            await sendData({
              url: `/media/${ci.id}`,
              method: 'delete',
            });
          })
        );
        set(formState, 'images', []);
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    };

    const closetask = () => {
      clickComment({ newStatus: 'closed', body: 'Task closed' });
      submitComment();
    };

    const completeCloseout = async () => {
      await deleteImages();
      closetask();
      closeConfirm();
    };

    if (status.status !== 1) {
      //author direct closeout... special conditions
      if (dontAskTaskDelete) {
        completeCloseout();
      } else {
        setConfirmProps({
          ...confirmProps,
          title: 'Are you sure?',
          content: 'You are closing a task without uploading correction photos first, this will also delete all the task photos. Would you like to continue?',
          okayText: 'Closeout and Delete Photos',
          showDontAsk: true,
          onCancel: closeConfirm,
          onOk: async (dontAsk) => {
            dispatch(setView({ dontAskTaskDelete: dontAsk }));
            await completeCloseout();
          },
        });
      }
    } else {
      closetask();
    }
  };

  const commentDates = () => {
    return `last updated ${formatDateFromNow(get(first(comments), 'createdAt', new Date()))}`;
  };

  const isForwardTransition = (() => {
    const newStatus = get(createComment, 'newStatus');
    if (!newStatus) return false;
    const newNum = taskStringToNum(newStatus);
    return newNum > status.status;
  })();
  const getCloseoutReq = (key) => {
    // Check current form state first (includes task type defaults merged with per-task overrides)
    const formMeta = get(formState, 'meta');
    if (formMeta && formMeta[key] !== undefined) return formMeta[key];
    // When a task type is available, its closeout_requirements are authoritative
    if (taskType) {
      return get(taskType, `closeout_requirements.${key}`, false);
    }
    // Legacy fallback for tasks without a task type
    const taskMeta = get(props, 'task.meta', {});
    if (taskMeta[key] !== undefined) return taskMeta[key];
    const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    return get(props, `task.meta.${camelKey}`, false);
  };
  const notesRequired = isForwardTransition && getCloseoutReq('notes_required');
  const photosRequired = isForwardTransition && getCloseoutReq('photos_required');
  const signatureRequired = isForwardTransition && getCloseoutReq('signature_required');

  const commentDoneDisabled = () => {
    const bodysize = size((get(createComment, 'body', '') || '').trim());
    const hasImages = size(get(createComment, 'images'));
    const hasFiles = size(get(createComment, 'files', []));
    const newNum = taskStringToNum(get(createComment, 'newStatus'));
    const isStatusChange = newNum !== status.status;

    // Prevent completely empty comments when there is no status change
    if (!isStatusChange && !bodysize && !hasImages && !hasFiles) {
      return {
        disabled: true,
        text: 'Add notes or images to save this comment',
      };
    }

    if (isForwardTransition) {
      // Check custom field requirements from task type
      // Only validate when the full form is available (showComments=true means full TaskDetail context)
      if (taskType && showComments) {
        // Merge persisted task values with current form edits
        const persisted = get(props, 'task.customFieldValues',
          get(props, 'task.custom_field_values',
            get(props, 'task.meta.custom_field_values', {})));
        const formEdits = {};
        Object.keys(formState || {}).forEach((k) => {
          if (k.startsWith('custom_field_values.')) {
            formEdits[k.replace('custom_field_values.', '')] = formState[k];
          }
        });
        const customFieldValues = { ...persisted, ...formEdits };
        const result = validateRequiredCustomFields(taskType, customFieldValues);
        if (!result.valid) {
          const labels = result.missingFields.map((f) => f.label).join(', ');
          return {
            disabled: true,
            text: `Required fields unfilled: ${labels}`,
            missingFields: result.missingFields,
          };
        }
      }

      const hasSig = !!(createComment?.signature?.data);
      const missingNotes = notesRequired && !bodysize;
      const missingPhotos = photosRequired && !hasImages;
      const missingSig = signatureRequired && !hasSig;

      if (missingNotes || missingPhotos || missingSig) {
        const parts = [];
        if (missingNotes) parts.push('notes');
        if (missingPhotos) parts.push('images');
        if (missingSig) parts.push('signature');
        return {
          disabled: true,
          text: `${parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')} required to change the status of this task`,
        };
      } else {
        return {
          disabled: false,
          text: 'Change the status of this task',
        };
      }
    } else {
      // No requirements to re-open a task from closed or pending (always allowed)
      return {
        disabled: false,
        text: 'Re-open this task',
      };
    }
  };

  const allImages = () => {
    const commentHas = flatten(comments.filter((c) => !!size(c.images)).map((c) => c.images));
    const taskHas = get(data, '0.images', []);

    return [...(commentHas || []), ...(taskHas || [])];
  };

  useEffect(() => {
    if (props.openInCreate) {
      clickComment(props.openInCreate);
    }
  }, []);

  useEffect(() => {
    const disp = !state.display ? 'comment' : state.display;
    const c =
      disp === 'all'
        ? comments
        : comments.filter((c) => {
            const isHist = !!get(c, 'meta.valuesChanged');
            const isCorr = !!get(c, 'meta.showCorrection');
            if (disp === 'comment') {
              return !isHist;
            } else if (disp === 'history') {
              return isHist || isCorr;
            }
          });
    setState((s) => ({ ...s, comments: c, display: disp }));
  }, [comments, state.display]);

  const showComments = get(props, 'showComments', true);

  const handleCommentDeleted = (id) => {
    data.update();
  };

  return (
    <div className={classes.commentContainer}>
      {showComments && (
        <div className={classes.commentsWrap}>
          <div className={classes.commentWrapTitle}>
            <Text type="title_large">Activity</Text>
            <div className={classes.commentCount}>{commentDates()}</div>
          </div>
          <div className={classes.showWrap}>
            <Row>
              <Text>Show: </Text>
              <ToggleButtonGroup color="secondary" size="small" value={state.display} onChange={(e, v) => setState((s) => ({ ...s, display: v }))} exclusive={true}>
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="comment">Comments</ToggleButton>
                <ToggleButton value="history">History</ToggleButton>
              </ToggleButtonGroup>
            </Row>
          </div>
          {state.comments.map((c, i) => (
            <Comment key={i} {...c} user={me} isAdmin={userHasPermissions(['manageAccount'])} onDeleted={() => handleCommentDeleted(c.id)} />
          ))}
        </div>
      )}
      {createComment && (() => {
        const commentFormContent = (inModal) => (
          <div className={inModal ? classes.createCommentContainerModal : classes.createCommentContainer}>
            {!props.isModal && !inModal && <div className={classes.leftIcon}>{getTaskStatus(createComment.newStatus).icon}</div>}

            <div className={inModal ? classes.textContainerMobile : classes.textContainer}>
              <div>
                <div className={classes.titleContainer}>{createComment && createComment.isCorrection ? 'Add Correction' : 'Add Comment'}</div>

                {createComment.showToggle ? (
                  <div className={classes.correctionToggleContainer}>
                    <ToggleButtonGroup fullWidth={true} size={'small'} onChange={(e, newStatus) => setCreateComment({ ...createComment, newStatus })} exclusive={true} value={createComment.newStatus}>
                      <ToggleButton value="pending">
                        <Icon className={classes.correctionBtnIcon}>check_circle_outline</Icon> Pending
                      </ToggleButton>
                      <ToggleButton value="closed" disabled={ate.disabled}>
                        <Icon className={classes.correctionBtnIcon}>check_circle_fill</Icon> Closed
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                ) : createComment && createComment.isCorrection ? (
                  <Row>
                    <Text>Change Status To</Text>
                    <div style={{ marginLeft: 5 }}>{getTaskStatus(createComment.newStatus).icon}</div>
                    <Text>{upperFirst(createComment.newStatus)}</Text>
                  </Row>
                ) : null}

                <TextField
                  multiline={true}
                  value={createComment.body}
                  maxRows={5}
                  variant="outlined"
                  fullWidth={true}
                  className={classes.commentText}
                  onChange={(e) => setCreateComment({ ...createComment, body: e.target.value })}
                  placeholder={`${notesRequired ? '* ' : ''}Add notes here`}
                />
              </div>

              <div>
                <Form
                  showAllScreens={true}
                  isNoMarginContainer
                  onFormState={(vals) => {
                    if (vals.commentMedia !== undefined) {
                      const { images, documents } = splitMediaByType(vals.commentMedia);
                      set(createComment, 'images', images);
                      set(createComment, 'files', documents);
                      setCreateComment({ ...createComment });
                    }
                  }}
                  screens={[{
                    title: '',
                    style: { marginRight: 0 },
                    inputs: [
                      photoDrop && {
                        id: 'commentMedia',
                        type: 'DropArea',
                        props: {
                          uploadAllNew: true,
                          collection: 'task-images',
                          bothMode: true,
                          accept: null,
                          notAllowedLocal: [],
                        },
                      },
                    ].filter(Boolean),
                  }]}
                />

                {signatureRequired && (
                  <SignaturePad
                    value={get(createComment, 'signature')}
                    onChange={(sig) => setCreateComment({ ...createComment, signature: sig })}
                    error={signatureRequired && !get(createComment, 'signature.data')}
                  />
                )}

                {createComment && createComment.isCorrection && (
                  <>
                    <Form
                      showAllScreens={true}
                      isNoMarginContainer
                      onFormState={(vals) => setEmailFormState((s) => ({ ...s, ...vals }))}
                      screens={[
                        {
                          title: '',
                          style: { marginRight: 0 },
                          inputs: [
                            emailBehavior !== 'always_send' && emailBehavior !== 'never_send' && {
                              id: '_sendReceipt',
                              component: () => (
                                <Checkbox label={'Send Email Receipt to:'} checked={sendReceipt} onChange={() => setSendReceipt(!sendReceipt)} />
                              ),
                              style: { marginBottom: 0 },
                            },
                            emailBehavior !== 'never_send' && (sendReceipt || emailBehavior === 'always_send') && {
                              id: 'emailRecipients',
                              type: 'UserSelect',
                              defaultValue: () => uniqBy([...get(props, 'task.authors', []), ...get(props, 'task.assignees', [])], 'id'),
                              data: () => usersList.data,
                              props: { multiple: true, label: emailBehavior === 'always_send' ? 'Email Receipt will be sent to' : 'Email' },
                              style: { paddingTop: 0 },
                            },
                            {
                              id: 'newAssignee',
                              type: 'UserSelect',
                              defaultValue: () => get(props, 'task.assignees', []),
                              data: () => usersList.data,
                              props: { multiple: true, label: 'Change Assignee' },
                            },
                          ].filter(Boolean),
                        },
                      ]}
                    />
                  </>
                )}

                <div className={classes.submitButtons}>
                  <Button
                    variant={'contained'}
                    color="primary"
                    startIcon={<Icon>close</Icon>}
                    onClick={() => {
                      if (!showComments) {
                        return props.onClose();
                      }
                      setCreateComment();
                    }}
                  >
                    Cancel
                  </Button>

                  <Tooltip title={commentDoneDisabled().text}>
                    <span>
                      <Button variant={'contained'} loading={state.loading} color="secondary" startIcon={<Icon>check</Icon>} disabled={commentDoneDisabled().disabled} onClick={() => {
                        if (commentDoneDisabled().disabled && commentDoneDisabled().missingFields && onCustomFieldErrors) {
                          onCustomFieldErrors(commentDoneDisabled().missingFields);
                        } else {
                          submitComment();
                        }
                      }}>
                        Done
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        );

        if (isMobile) {
          const closeCommentDialog = () => {
            if (!showComments) {
              return props.onClose();
            }
            setCreateComment();
          };
          return (
            <Dialog open={true} fullScreen onClose={closeCommentDialog}>
              <AppBar color="primary" position="relative">
                <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={closeCommentDialog} size="large">
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="h6" style={{ flex: 1 }}>
                    {createComment.isCorrection ? 'Status Change' : 'Add Comment'}
                  </Typography>
                </Toolbar>
              </AppBar>
              <div style={{ padding: 16 }}>
                {!showComments && task && (
                  <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                    <Row style={{ alignItems: 'center', marginBottom: 4 }}>
                      <Icon style={{ marginRight: 8 }}>{status.icon}</Icon>
                      <Text type="title_medium">{get(task, 'name', '')}</Text>
                    </Row>
                    {get(task, 'description') && (
                      <Text style={{ color: '#666', fontSize: 13 }}>{get(task, 'description')}</Text>
                    )}
                  </div>
                )}
                {commentFormContent(true)}
              </div>
            </Dialog>
          );
        }

        return commentFormContent(false);
      })()}
      {!createComment && (
        <div className={classes.commentBtnContainer}>
          <Button
            className={classes.commentBtn}
            variant={'contained'}
            color="secondary"
            startIcon={<Icon>add_comment</Icon>}
            onClick={() => clickComment({ newStatus: status.title, isCorrection: false })}
          >
            Comment
          </Button>

          {[1, 2].includes(status.status) && (
            <Tooltip title={`Change the task status to "Open".`}>
              <span>
                <Button
                  className={classes.commentBtn}
                  variant={'contained'}
                  color="secondary"
                  disabled={ate.disabled}
                  startIcon={getTaskStatus('open').icon}
                  onClick={() => clickComment({ newStatus: 'open' })}
                >
                  Open
                </Button>
              </span>
            </Tooltip>
          )}

          {[0, 2].includes(status.status) && (
            <Tooltip title={`Change the task status to "Pending".`}>
              <span>
                <Button
                  className={classes.commentBtn}
                  variant={'contained'}
                  color="secondary"
                  disabled={ate.disabled}
                  startIcon={getTaskStatus('pending').icon}
                  onClick={() => clickComment({ newStatus: 'pending' })}
                >
                  Pending
                </Button>
              </span>
            </Tooltip>
          )}

          {[0, 1].includes(status.status) && (
            <Tooltip title={`Change the task status to "Closed".`}>
              <span>
                <Button
                  className={classes.commentBtn}
                  variant={'contained'}
                  color="secondary"
                  startIcon={getTaskStatus('closed').icon}
                  disabled={ate.disabled}
                  onClick={() => clickComment({ newStatus: 'closed' })}
                >
                  Close
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      )}
      <Confirm {...confirmProps} />
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
};
